/**
 * <ImageDisplay> — a full-screen image viewer (molecule, L0.1).
 *
 * Dark `<Overlay>` showing ONE image at a time (`resizeMode="contain"`) inside `<PinchZoom>`
 * (pinch/pan/rotate), with Overlay close chrome, `n / total` counter, prev/next, rotate, and
 * web-only `+/-` zoom. Composed by the `<MediaDisplay>` organism for the `image` kind.
 */
import React, { useEffect, useRef, useState } from "react"
import { Image, Platform, View } from "react-native"
import { SvgXml } from "react-native-svg"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import { StyleSheet } from "react-native-unistyles"

import { Overlay } from "../../atoms/Overlay"
import { PinchZoom, type PinchZoomHandle } from "../../atoms/PinchZoom"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { Badge } from "../../atoms/Badge"
import { Row } from "../../atoms/containers/Row"

/** Une image par son adresse ; ou une figure SVG (`svg`), dessinée telle quelle, l'adresse ignorée. */
type ViewImage = { uri: string; svg?: string }

export type ImageDisplayProps = {
    visible: boolean
    images: ViewImage[]
    /** Which image to open on first render. Defaults to 0. */
    initialIndex?: number
    onClose: () => void
    /**
     * « Supprimer », en haut à côté du compteur : l'image montrée (son index) est à retirer. La
     * confirmation est à l'appelant. Absent (défaut) : la visionneuse ne retire rien.
     */
    onDelete?: (index: number) => void
    testID?: string
}

const IS_WEB = Platform.OS === "web"

const TEXTS = { delete: "Supprimer" }

export function ImageDisplay(props: ImageDisplayProps) {
    const { visible, images, initialIndex = 0, onClose, onDelete, testID } = props

    const insets = useSafeAreaInsets()
    const [index, setIndex] = useState(initialIndex)
    const zoomRef = useRef<PinchZoomHandle>(null)

    useEffect(() => {
        if (visible) setIndex(initialIndex)
    }, [visible, initialIndex])

    const total = images.length
    const safeIndex = Math.min(Math.max(index, 0), Math.max(total - 1, 0))
    const current = images[safeIndex]
    const hasMultiple = total > 1

    return (
        <Overlay visible={visible} onRequestClose={onClose} withCloseButton placement="center" testID={testID}>
            <View style={styles.container}>
                {(hasMultiple || onDelete != null) && (
                    <View style={styles.header(insets.top)}>
                        <Row gap="3" align="center">
                            {hasMultiple && <Badge variant="neutral" text={`${safeIndex + 1} / ${total}`} />}
                            {onDelete != null && current != null && (
                                <Button
                                    variant="outline"
                                    color="danger"
                                    size="sm"
                                    iconName="trash"
                                    label={TEXTS.delete}
                                    onPress={() => onDelete(safeIndex)}
                                />
                            )}
                        </Row>
                    </View>
                )}

                {current != null && (
                    <PinchZoom
                        key={`${current.svg != null ? "svg" : current.uri}-${safeIndex}`}
                        ref={zoomRef}
                        style={styles.zoom}
                    >
                        {current.svg != null ? (
                            <SvgXml xml={current.svg} width="100%" height="100%" />
                        ) : (
                            <Image source={{ uri: current.uri }} resizeMode="contain" style={styles.flex1} />
                        )}
                    </PinchZoom>
                )}

                {current != null && (
                    <View style={styles.nav(insets.bottom)}>
                        <Row justify="center" style={styles.flex1}>
                            {IS_WEB && (
                                <IconButton
                                    iconName="search-minus"
                                    color="text-muted"
                                    size={44}
                                    onPress={() => zoomRef.current?.zoomOut()}
                                    accessibilityLabel="Dézoomer"
                                />
                            )}
                            <IconButton
                                iconName="redo"
                                color="text-muted"
                                size={44}
                                onPress={() => zoomRef.current?.rotate()}
                                accessibilityLabel="Pivoter"
                            />
                            {IS_WEB && (
                                <IconButton
                                    iconName="search-plus"
                                    color="text-muted"
                                    size={44}
                                    onPress={() => zoomRef.current?.zoomIn()}
                                    accessibilityLabel="Zoomer"
                                />
                            )}
                            {hasMultiple && (
                                <>
                                    <IconButton
                                        iconName="chevron-left"
                                        color="text-muted"
                                        size={44}
                                        disabled={safeIndex === 0}
                                        onPress={() => setIndex((i) => Math.max(0, i - 1))}
                                        accessibilityLabel="Image précédente"
                                    />
                                    <IconButton
                                        iconName="chevron-right"
                                        color="text-muted"
                                        size={44}
                                        disabled={safeIndex === total - 1}
                                        onPress={() => setIndex((i) => Math.min(total - 1, i + 1))}
                                        accessibilityLabel="Image suivante"
                                    />
                                </>
                            )}
                        </Row>
                    </View>
                )}
            </View>
        </Overlay>
    )
}

const styles = StyleSheet.create((theme) => ({
    container: {
        width: "95%" as const,
        height: "95%" as const,
        backgroundColor: theme.colors["black"],
    },
    header: (topInset: number) => ({
        position: "absolute" as const,
        top: topInset + theme.spacing["2"],
        left: theme.spacing["3"],
        zIndex: 10,
    }),
    zoom: {
        flex: 1,
        paddingBottom: theme.spacing["16"],
        paddingTop: theme.spacing["4"],
    },
    flex1: {
        flex: 1,
    },
    nav: (bottomInset: number) => ({
        position: "absolute" as const,
        left: theme.spacing["3"],
        right: theme.spacing["3"],
        bottom: bottomInset + theme.spacing["3"],
        zIndex: 10,
        flexDirection: "row" as const,
        alignItems: "center" as const,
        justifyContent: "space-between" as const,
    }),
}))
